import { formatLocaleNumber } from '../../../shared/localeFormatting.mjs';
import { useBoardLocale } from '@/components/board_portal/useBoardLocale';
import { boardDisplay } from '@/components/board_portal/boardPresentation';
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'
import { useMemberVotingApi } from '../api/voting'

const PIE_FALLBACK_COLORS = ['#16a34a', '#f97316', '#9333ea', '#38bdf8', '#ef4444']

function getAccentColor() {
  if (typeof window === 'undefined') return 'var(--mp-accent)'
  return getComputedStyle(document.documentElement).getPropertyValue('--mp-accent').trim() || 'var(--mp-accent)'
}

function formatCurrency(value, locale) {
  const number = Number(value || 0)
  return formatLocaleNumber(number, locale, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}


export default function MemberVotingDetailPage({ clubCode }) {
  const { t, locale, date } = useBoardLocale();
  const { motionId } = useParams()
  const navigate = useNavigate()
  const { getMotionDetail, submitVote } = useMemberVotingApi()
  const [motion, setMotion] = useState(null)
  const [tallies, setTallies] = useState(null)
  const [auditEvents, setAuditEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [voteStatus, setVoteStatus] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function loadMotion() {
      if (!clubCode || !motionId) return
      setLoading(true)
      setError(null)
      try {
        const data = await getMotionDetail(clubCode, motionId)
        if (!cancelled) {
          setMotion(data)
          setTallies(data?.tallies || null)
          setAuditEvents(Array.isArray(data?.audit_events) ? data.audit_events : [])
          setVoteStatus(data?.viewer_vote || null)
        }
      } catch (err) {
        if (!cancelled) setError(err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadMotion()
    return () => {
      cancelled = true
    }
  }, [clubCode, motionId, getMotionDetail])

  const pieColors = useMemo(() => [getAccentColor(), ...PIE_FALLBACK_COLORS], [])

  const allocationData = useMemo(() => {
    const items = motion?.allocation_snapshot?.items || []
    return items.map((item) => ({
      name: item.name,
      value: Number(item.total_amount) || 0,
    }))
  }, [motion])

  const totals = motion?.budget_snapshot?.totals || {}

  const handleVote = async (selection) => {
    if (!clubCode || !motionId) return
    setSubmitting(true)
    setError(null)
    try {
      await submitVote(clubCode, motionId, selection)
      setVoteStatus(selection)
    } catch (err) {
      setError(err)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div className="mp-voting mp-voting--detail"><div className="mp-card" aria-busy="true">{t('boardPortal.loadingMotion')}{' '}</div></div>
  }

  if (error && !motion) {
    return <div className="mp-voting mp-voting--detail"><div className="mp-card" role="alert">{t('boardPortal.unableToLoadMotionDetails')}{' '}</div></div>
  }

  return (
    <div className="mp-voting mp-voting--detail">
      <button type="button" className="mp-voting__back" onClick={() => navigate(`/${clubCode}/voting`)}>← {t('boardPortal.backToVoting')}{' '}</button>

      <div className="mp-voting__header">
        <div>
          <p className="mp-eyebrow">{t('boardPortal.motionReview')}{' '}</p>
          <h1>{motion?.title}</h1>
          <p className="mp-muted">{t('boardPortal.opens')}{' '}{date(motion?.opens_at, { dateStyle: 'medium', timeStyle: 'short', timeZone: motion?.presentation_timezone || 'UTC' })}{' · '}{t('boardPortal.closes')}{' '}{date(motion?.closes_at, { dateStyle: 'medium', timeStyle: 'short', timeZone: motion?.presentation_timezone || 'UTC' })}
          </p>
        </div>
        <div className="mp-voting__status">
          <span>{voteStatus ? t('boardPortal.votedChoice', { choice: boardDisplay(t, 'vote', voteStatus) }) : t('boardPortal.yourVoteNotRecordedYet')}</span>
        </div>
      </div>

      <div className="mp-voting__grid mp-voting__grid--detail">
        <div className="mp-card mp-voting__card">
          <h2>{t('boardPortal.motionTotals')}{' '}</h2>
          <div className="mp-voting__totals">
            <div>
              <p className="mp-muted">{t('boardPortal.totalBudget')}{' '}</p>
              <strong>{formatCurrency(totals.total_amount || 0, locale)}</strong>
            </div>
            <div>
              <p className="mp-muted">{t('boardPortal.monthlyAverage')}{' '}</p>
              <strong>{formatCurrency((totals.total_amount || 0) / 12, locale)}</strong>
            </div>
            <div>
              <p className="mp-muted">{t('boardPortal.votesSoFar')}{' '}</p>
              <strong>{tallies?.total || 0}</strong>
            </div>
          </div>
        </div>

        <div className="mp-card mp-voting__card">
          <h2>{t('boardPortal.allocationByDepartment')}{' '}</h2>
          {allocationData.length ? (
            <div className="mp-voting__chart">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie dataKey="value" data={allocationData} innerRadius={60} outerRadius={90}>
                    {allocationData.map((entry, index) => (
                      <Cell key={entry.name} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value, locale)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="mp-muted">{t('boardPortal.noAllocationDataAvailable')}{' '}</p>
          )}
        </div>

        <div className="mp-card mp-voting__card mp-voting__timeline">
          <h2>{t('boardPortal.governanceTimeline')}{' '}</h2>
          {auditEvents.length ? (
            <ul>
              {auditEvents.map((event) => (
                <li key={event.id}>
                  <span>{date(event.created_at, { dateStyle: 'medium', timeStyle: 'short', timeZone: motion?.presentation_timezone || 'UTC' })}</span>
                  <strong>{boardDisplay(t, 'action', event.action)}</strong>
                  {event.details?.actor_name ? ` · ${event.details.actor_name}` : ''}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mp-muted">{t('boardPortal.timelineUpdatesWillAppearHere')}{' '}</p>
          )}
        </div>
      </div>

      <div className="mp-card mp-voting__vote-panel">
        <h2>{t('boardPortal.castYourVote')}{' '}</h2>
        <div className="mp-voting__buttons">
          {['yes', 'no', 'abstain'].map((selection) => (
            <button
              key={selection}
              type="button"
              className={`mp-voting__vote ${voteStatus === selection ? 'is-active' : ''}`}
              onClick={() => handleVote(selection)}
              disabled={submitting}
            >
              {boardDisplay(t, 'vote', selection)}
            </button>
          ))}
        </div>
        {error && motion && <p className="mp-voting__error">{t('boardPortal.unableToRecordVotePleaseTryAgain')}{' '}</p>}
      </div>
    </div>
  )
}
